import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  FiSearch, FiClock, FiArrowRight, FiTag, FiCalendar, FiUser,
  FiChevronRight, FiBookOpen, FiPlus, FiEdit2, FiTrash2, FiSettings,
} from 'react-icons/fi';
import ConfirmModal from '../../components/ConfirmModal';
import NewsletterSignup from '../../components/NewsletterSignup';
import { BLOG_CATEGORIES } from '../../data/blogStore';
import { apiGetAllPosts, apiDeletePost } from '../../api/blogApi';
import { useAuth } from '../../context/AuthContext';

export default function BlogListing() {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const isAdmin = user?.isAdmin;
  const [posts, setPosts] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [activeTag, setActiveTag] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadPosts = () => {
    apiGetAllPosts({ status: 'published' }).then((res) => {
      if (res.ok) {
        setPosts(res.data);
        setFeatured(res.data.filter((p) => p.featured));
      }
    });
  };

  useEffect(() => {
    loadPosts();
    const cat = searchParams.get('category');
    if (cat) setActiveCategory(cat);
    const tag = searchParams.get('tag');
    if (tag) setActiveTag(tag);
  }, [searchParams]);

  const handleDeletePost = async () => {
    if (!deleteTarget) return;
    await apiDeletePost(deleteTarget);
    setDeleteTarget(null);
    loadPosts();
  };

  const filtered = useMemo(() => {
    let result = posts;
    if (activeCategory !== 'All') {
      result = result.filter((p) => p.category === activeCategory);
    }
    if (activeTag) {
      result = result.filter((p) => p.tags.includes(activeTag));
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (p) =>
          p.title.toLowerCase().includes(q) ||
          p.excerpt.toLowerCase().includes(q) ||
          p.tags.some((t) => t.includes(q))
      );
    }
    return result;
  }, [posts, activeCategory, activeTag, search]);

  /* Collect all tags from visible posts */
  const popularTags = useMemo(() => {
    const counts = {};
    posts.forEach((p) => p.tags.forEach((t) => { counts[t] = (counts[t] || 0) + 1; }));
    return Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 12).map(([t]) => t);
  }, [posts]);

  return (
    <div className="blog-page">
      {/* Hero */}
      <section className="blog-hero">
        <div className="blog-hero-inner">
          <div className="blog-hero-badge"><FiBookOpen /> Engineering Log</div>
          <h1>Insights, Experiments &<br />Engineering Breakdowns</h1>
          <p className="blog-hero-sub">
            Behind-the-scenes engineering from Vector Labs — case studies, technical deep-dives,
            industry analysis, and lessons from building production AI systems.
          </p>
          <div className="blog-search-hero">
            <FiSearch />
            <input
              type="text"
              placeholder="Search articles..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {isAdmin && (
            <div className="inline-admin-bar" style={{ marginTop: 20 }}>
              <span className="inline-admin-badge"><FiSettings /> Admin Mode</span>
              <Link to="/admin/blog/new" className="inline-admin-btn primary">
                <FiPlus /> New Article
              </Link>
              <Link to="/admin/blog" className="inline-admin-btn secondary">
                <FiSettings /> Blog Dashboard
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Featured Posts */}
      {!search && activeCategory === 'All' && !activeTag && featured.length > 0 && (
        <section className="blog-featured">
          <div className="blog-container">
            <h2 className="blog-section-title">Featured</h2>
            <div className="blog-featured-grid">
              {featured.map((post) => (
                <Link to={`/blog/${post.slug}`} key={post.id} className="blog-featured-card">
                  <div className="blog-featured-img">
                    <img src={post.coverImage} alt={post.title} loading="lazy" />
                    <span className="blog-card-category" style={{ background: categoryColor(post.category) }}>
                      {post.category}
                    </span>
                  </div>
                  <div className="blog-featured-body">
                    <h3>{post.title}</h3>
                    <p>{post.excerpt}</p>
                    <div className="blog-card-meta">
                      <span><FiUser /> {post.author}</span>
                      <span><FiCalendar /> {formatDate(post.publishedDate)}</span>
                      <span><FiClock /> {post.readTime} min read</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Main Content */}
      <section className="blog-main">
        <div className="blog-container blog-layout">
          {/* Articles */}
          <div className="blog-articles">
            {/* Category Pills */}
            <div className="blog-category-pills">
              <button
                className={activeCategory === 'All' ? 'active' : ''}
                onClick={() => { setActiveCategory('All'); setActiveTag(null); }}
              >
                All
              </button>
              {BLOG_CATEGORIES.map((cat) => (
                <button
                  key={cat}
                  className={activeCategory === cat ? 'active' : ''}
                  onClick={() => { setActiveCategory(cat); setActiveTag(null); }}
                >
                  {cat}
                </button>
              ))}
            </div>

            {activeTag && (
              <div className="blog-active-tag">
                Filtered by tag: <strong>{activeTag}</strong>
                <button onClick={() => setActiveTag(null)}>Clear</button>
              </div>
            )}

            {/* Post Cards */}
            {filtered.length === 0 && (
              <div className="blog-empty">
                <p>No articles found. Try a different search or category.</p>
              </div>
            )}

            {filtered.map((post) => (
              <article key={post.id} className="blog-card">
                {isAdmin && (
                  <div className="blog-card-admin-actions">
                    <Link to={`/admin/blog/edit/${post.id}`} className="paper-admin-btn edit" title="Edit">
                      <FiEdit2 />
                    </Link>
                    <button className="paper-admin-btn delete" onClick={(e) => { e.preventDefault(); setDeleteTarget(post.id); }} title="Delete">
                      <FiTrash2 />
                    </button>
                  </div>
                )}
                <Link to={`/blog/${post.slug}`} className="blog-card-link">
                  <div className="blog-card-image">
                    <img src={post.coverImage} alt={post.title} loading="lazy" />
                  </div>
                  <div className="blog-card-content">
                    <div className="blog-card-top">
                      <span className="blog-card-cat" style={{ color: categoryColor(post.category) }}>
                        {post.category}
                      </span>
                      <span className="blog-card-date"><FiCalendar /> {formatDate(post.publishedDate)}</span>
                    </div>
                    <h3>{post.title}</h3>
                    <p>{post.excerpt}</p>
                    <div className="blog-card-bottom">
                      <div className="blog-card-author">
                        <FiUser /> {post.author}
                      </div>
                      <div className="blog-card-read">
                        <FiClock /> {post.readTime} min read
                      </div>
                      <div className="blog-card-arrow">
                        Read More <FiArrowRight />
                      </div>
                    </div>
                    <div className="blog-card-tags">
                      {post.tags.slice(0, 4).map((t) => (
                        <span key={t} className="blog-tag-chip"><FiTag /> {t}</span>
                      ))}
                    </div>
                  </div>
                </Link>
              </article>
            ))}
          </div>

          {/* Sidebar */}
          <aside className="blog-sidebar">
            {/* About */}
            <div className="blog-sidebar-card">
              <h4>About This Blog</h4>
              <p>
                The Engineering Log is Vector Labs' public journal. We share what we're building,
                what we've learned, and how we think about AI, software engineering, and business operations.
              </p>
              <Link to="/consulting" className="blog-sidebar-cta">
                Work With Us <FiChevronRight />
              </Link>
            </div>

            {/* Popular Tags */}
            <div className="blog-sidebar-card">
              <h4>Popular Topics</h4>
              <div className="blog-sidebar-tags">
                {popularTags.map((t) => (
                  <button
                    key={t}
                    className={`blog-sidebar-tag ${activeTag === t ? 'active' : ''}`}
                    onClick={() => setActiveTag(activeTag === t ? null : t)}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            {/* Newsletter CTA */}
            <div className="blog-sidebar-card blog-sidebar-newsletter">
              <h4>Engineering Updates</h4>
              <p>Notes on what we are building. No account required.</p>
              <NewsletterSignup id="blog-newsletter-email" />
            </div>

            {/* Categories */}
            <div className="blog-sidebar-card">
              <h4>Categories</h4>
              <ul className="blog-sidebar-categories">
                {BLOG_CATEGORIES.map((cat) => {
                  const count = posts.filter((p) => p.category === cat).length;
                  return (
                    <li key={cat}>
                      <button onClick={() => { setActiveCategory(cat); setActiveTag(null); }}>
                        {cat} <span>({count})</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </aside>
        </div>
      </section>

      {/* SEO Footer */}
      <section className="blog-seo-footer">
        <div className="blog-container">
          <h2>Vector Labs Engineering Log</h2>
          <p>
            Technical articles on AI automation, software engineering, CRM optimization, data pipelines,
            and enterprise consulting from the engineering team at Vector Labs — an applied AI research
            and engineering lab based in Charlotte, NC. We publish case studies, tutorials, industry insights,
            and engineering breakdowns to help B2B companies adopt AI faster and build better software.
          </p>
          <div className="blog-seo-tags">
            {['AI consulting Charlotte NC', 'software engineering blog', 'AI automation case studies',
              'B2B AI consulting', 'CRM automation', 'data pipeline architecture', 'LLM deployment',
              'enterprise AI', 'machine learning consulting', 'rapid engineering teams'].map((t) => (
              <span key={t}>{t}</span>
            ))}
          </div>
        </div>
      </section>

      <ConfirmModal
        open={!!deleteTarget}
        title="Delete Article"
        message="This will permanently delete this blog article.\nThis action cannot be undone."
        confirmLabel="Delete"
        variant="danger"
        onConfirm={handleDeletePost}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}

/* Helpers */
function formatDate(dateStr) {
  if (!dateStr) return 'Unpublished';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function categoryColor(cat) {
  const colors = {
    'Engineering': '#6cb4ff',
    'AI & Automation': '#a78bfa',
    'Company Updates': '#34d399',
    'Case Studies': '#fbbf24',
    'Industry Insights': '#f87171',
    'Product Launches': '#22d3ee',
    'Tutorials & Guides': '#818cf8',
  };
  return colors[cat] || '#94a3b8';
}
